import { useEffect, useMemo, useState } from 'react';
import { ListOrdered } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { gameService } from '../../../services/gameService';
import { matchPointsService } from '../../../services/tennis/matchPointsService';
import { projectTennisMatchStats } from '../../../services/tennis/statsProjection';
import { calculateTennisStats } from '../../../utils/statCalculations';
import { getTennisMatchFormat } from '../../../data/tennisMatchFormats';
import { reduce } from '../../../services/tennis/scoring';
import { PointLogSheet } from './scoring/PointLog';
import { TennisStatCategorySections } from './stats/TennisStatTiles';
import type { Game, MatchParticipant, MatchPoint } from '../../../types';

interface TennisMatchDetailProps {
  game: Game;
  gameId: string;
}

interface RosterPlayer {
  id: string;
  firstName: string;
  lastName: string;
}

/**
 * Tennis's own match detail view (sc-339 follow-up) — the Phase 1 TODO of
 * splitting GameDetailView per sport, finally done for Tennis. Shows only
 * the players who actually played this match (1 singles participant, or 2
 * doubles partners each with their own stat tiles), not the full roster —
 * stats here are projected live from the point log, not manually entered,
 * so GenericSportGameDetail's "click a roster player to add stats" UI never
 * fit Tennis to begin with.
 */
export function TennisMatchDetail({ game, gameId }: TennisMatchDetailProps) {
  const [participants, setParticipants] = useState<MatchParticipant[]>([]);
  const [players, setPlayers] = useState<RosterPlayer[]>([]);
  const [points, setPoints] = useState<MatchPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPointLog, setShowPointLog] = useState(false);

  const isDoubles = game.match_type === 'Doubles' || game.match_type === 'Mixed Doubles';
  const format = useMemo(() => getTennisMatchFormat(game.match_format?.id as string | undefined), [game.match_format]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [participantRows, pointRows] = await Promise.all([
          gameService.getMatchParticipants(gameId),
          matchPointsService.getPoints(gameId),
        ]);
        if (cancelled) return;
        setParticipants(participantRows);
        setPoints(pointRows);

        if (participantRows.length > 0) {
          const { data: playerRows } = await supabase
            .from('players')
            .select('id, first_name, last_name')
            .in('id', participantRows.map((p) => p.player_id));
          if (!cancelled && playerRows) {
            const bySlot = new Map(participantRows.map((p) => [p.player_id, p.slot]));
            setPlayers(
              [...playerRows]
                .sort((a, b) => (bySlot.get(a.id) ?? 0) - (bySlot.get(b.id) ?? 0))
                .map((p) => ({ id: p.id, firstName: p.first_name, lastName: p.last_name }))
            );
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  const ourName = useMemo(() => {
    if (players.length === 0) return 'Us';
    if (!isDoubles) return `${players[0].firstName} ${players[0].lastName}`;
    return players.map((p) => p.firstName).join(' / ');
  }, [players, isDoubles]);
  const opponentName = game.opponent_name || 'Opponent';

  const projection = useMemo(
    () =>
      projectTennisMatchStats({
        format,
        points,
        isDoubles,
        participantPlayerIds: participants.map((p) => p.player_id),
      }),
    [format, points, isDoubles, participants]
  );

  const matchState = useMemo(() => reduce(format, points.map((p) => ({ winner: p.won_by })), { isDoubles }), [format, points, isDoubles]);

  if (loading) {
    return <p className="text-sm text-gray-500">Loading match…</p>;
  }

  if (participants.length === 0) {
    return <p className="text-sm text-gray-500">No players were recorded for this match.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="border border-gray-200 rounded-lg overflow-hidden text-sm flex-1 min-w-[220px]">
          <div className="flex divide-x divide-gray-200">
            <div className="flex-1 min-w-0 divide-y divide-gray-100">
              <div className="px-3 py-2 font-semibold text-gray-900 truncate">{ourName}</div>
              <div className="px-3 py-2 font-semibold text-gray-900 truncate">{opponentName}</div>
            </div>
            {matchState.sets.map((s, i) => (
              <div key={i} className="w-9 divide-y divide-gray-100 flex-shrink-0">
                <div className={`py-2 text-center tabular-nums ${s.ourGames > s.theirGames ? 'font-bold text-gray-900' : 'text-gray-400'}`}>{s.ourGames}</div>
                <div className={`py-2 text-center tabular-nums ${s.theirGames > s.ourGames ? 'font-bold text-gray-900' : 'text-gray-400'}`}>{s.theirGames}</div>
              </div>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowPointLog(true)}
          className="btn-secondary text-sm flex items-center gap-1.5 flex-shrink-0"
        >
          <ListOrdered size={16} />
          View Point Log
        </button>
      </div>

      {points.length === 0 ? (
        <p className="text-sm text-gray-500">No points have been scored for this match yet.</p>
      ) : isDoubles ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {players.map((p) => {
            const totals = projection[p.id] ?? {};
            const calc = calculateTennisStats(Object.entries(totals).map(([stat_key, stat_value]) => ({ stat_key, stat_value })));
            return (
              <div key={p.id} className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-bold text-gray-900 mb-3">{p.firstName} {p.lastName}</h3>
                <TennisStatCategorySections totals={totals} calc={calc} />
              </div>
            );
          })}
        </div>
      ) : (
        (() => {
          const totals = projection[players[0].id] ?? {};
          const calc = calculateTennisStats(Object.entries(totals).map(([stat_key, stat_value]) => ({ stat_key, stat_value })));
          return <TennisStatCategorySections totals={totals} calc={calc} />;
        })()
      )}

      <PointLogSheet
        open={showPointLog}
        onClose={() => setShowPointLog(false)}
        points={points.map((p) => ({ ...p, synced: true }))}
        format={format}
        isDoubles={isDoubles}
        ourName={ourName}
        opponentName={opponentName}
        ourPlayers={players}
      />
    </div>
  );
}

export default TennisMatchDetail;
